import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import SectionHeader from '../SectionHeader/SectionHeader';

const plans = [
  {
    name: "Basic",
    badge: null,
    price: "Basic",
    target: "Small businesses & startups",
    features: [
      "Single web application",
      "Basic database design",
      "3 months support",
      "Standard security",
      "1 revision round"
    ],
    cta: "Get Started",
    ctaStyle: "text-[#EFCD86] border-2 border-[#EFCD86] hover:bg-[#EFCD86] hover:text-[#0a1628]",
    gradientFrom: "rgba(4, 106, 140, 0.4)", // Teal
    gradientTo: "rgba(13, 31, 51, 0)",      // Dark
  },
  {
    name: "Standard",
    badge: "Popular",
    price: "Standard",
    target: "Growing businesses",
    features: [
      "Full-stack web + mobile app",
      "Advanced database & API",
      "6 months priority support",
      "Enhanced security protocols",
      "3 revision rounds",
      "Basic AI integration"
    ],
    cta: "Go Standard",
    ctaStyle: "bg-[#66B152] text-white hover:brightness-110",
    gradientFrom: "rgba(102, 177, 82, 0.4)", // Green
    gradientTo: "rgba(4, 106, 140, 0)",      // Teal
  },
  {
    name: "Premium",
    badge: null,
    price: "Premium",
    target: "Large enterprises",
    features: [
      "Custom software suite",
      "ERP + AI/ML solutions",
      "12 months dedicated support",
      "Enterprise security & compliance",
      "Unlimited revisions",
      "Advanced AI/ML integration",
      "24/7 maintenance"
    ],
    cta: "Go Premium",
    ctaStyle: "bg-[#EFCD86] text-[#0a1628] font-bold hover:brightness-110",
    gradientFrom: "rgba(239, 205, 134, 0.4)", // Gold
    gradientTo: "rgba(4, 106, 140, 0)",       // Teal
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const cardMotion = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const PricingCard = ({ plan }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <motion.div
      variants={cardMotion}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative flex flex-col rounded-3xl bg-[#0d1f33] border border-white/10 overflow-hidden backdrop-blur-sm shadow-xl"
    >
      {/* Background Gradient overlay on hover */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ease-out ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        style={{
          background: `linear-gradient(to top, ${plan.gradientFrom}, ${plan.gradientTo}, transparent)`
        }}
      />

      {/* Card glowing border on hover */}
      <div className={`absolute inset-0 rounded-3xl pointer-events-none border-2 transition-colors duration-500 ease-out ${isHovered ? 'border-[#EFCD86]/80' : 'border-transparent'}`} />

      {/* Card Content */}
      <div className="relative z-10 p-8 flex flex-col h-full">
        {/* Header containing the AnimatePresence swap */}
        <div className="flex justify-between items-start mb-6 h-16">
          <div className="relative w-full">
            <div className="h-10 relative">
              <AnimatePresence>
                {!isHovered ? (
                  <motion.h3
                    key="name"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="text-3xl font-bold text-white absolute inset-0"
                  >
                    {plan.name}
                  </motion.h3>
                ) : (
                  <motion.div
                    key="price"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="text-3xl font-bold text-[#EFCD86] absolute inset-0 flex items-baseline gap-1 tracking-tight"
                  >
                    {plan.price}
                    {plan.price !== "Custom" && <span className="text-xl text-[#EFCD86] font-medium tracking-normal">/proj</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <p className="text-sm text-gray-400 mt-2">{plan.target}</p>
          </div>

          {plan.badge && (
            <div className="px-3 py-1 bg-[#66B152]/20 border border-[#66B152] text-[#66B152] text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 shadow-[0_0_10px_rgba(102,177,82,0.3)] shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-[#66B152] animate-pulse"></span>
              {plan.badge}
            </div>
          )}
        </div>

        <div className="w-full h-px bg-white/10 mb-8" />

        {/* Features */}
        <ul className="flex-grow space-y-4 mb-10">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="mt-1 w-5 h-5 rounded-full bg-[#046A8C]/20 flex items-center justify-center flex-shrink-0">
                <Check size={12} className="text-[#046A8C]" />
              </div>
              <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          onClick={() => navigate('/contact')}
          className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 mt-auto ${plan.ctaStyle}`}
        >
          {plan.cta}
        </button>
      </div>
    </motion.div>
  );
};

export default function Pricing() {
  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0e3a63] relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHeader
          badge="Pricing"
          title="A Plan for Every Need"
          subtitle="MakSoft delivers tailored software solutions that align with your business goals, offering flexible engagement models for startups to enterprises."
          accentColor="gold"
        />

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch mt-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
        >
          {plans.map((plan) => (
            <PricingCard key={plan.name} plan={plan} />
          ))}
        </motion.div>
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#046A8C] opacity-[0.03] rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
}

